import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { IconPlus, IconTrash, IconChevronUp, IconChevronDown, IconGripVertical, IconLanguage } from '@tabler/icons-react';
import { getSortedTranslations } from '../../lib/languageUtils';
import TranslationRow from './TranslationRow';
import { useEffect, useState } from 'react';
import { showToast } from '../../lib/toast';

const SentenceCard = ({ 
    sentence, 
    sentenceIndex, 
    totalSentences,
    contentManagement 
}) => {
    const [translationsExpanded, setTranslationsExpanded] = useState(false);
    const {
        translatingIndexes,
        translationError,
        deleteSentence,
        moveSentence,
        updateSentenceContent,
        addTranslation,
        deleteTranslation,
        updateTranslation,
        translateSentence
    } = contentManagement;

    // Show translation error toast
    useEffect(() => {
        if (translationError) {
            showToast.error(translationError);
        }
    }, [translationError]);

    return (
        <Card className="border-l-4 border-l-primary/20">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <IconGripVertical className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Sentence {sentenceIndex + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSentence(sentenceIndex, 'up')}
                            disabled={sentenceIndex === 0}
                        >
                            <IconChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSentence(sentenceIndex, 'down')}
                            disabled={sentenceIndex === totalSentences - 1}
                        >
                            <IconChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSentence(sentenceIndex)}
                            className="text-destructive hover:text-destructive"
                        >
                            <IconTrash className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Main Content */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground">Original Content</label>
                    <div className="flex gap-2">
                        <Input
                            value={sentence.content || ''}
                            onChange={(e) => updateSentenceContent(sentenceIndex, e.target.value)}
                            placeholder="Enter sentence content..."
                            className="flex-1"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => translateSentence(sentenceIndex)}
                            disabled={!sentence.content || sentence.content.trim() === '' || translatingIndexes.includes(sentenceIndex)}
                            className="shrink-0"
                        >
                            <IconLanguage className={`h-4 w-4 mr-1 ${translatingIndexes.includes(sentenceIndex) ? 'animate-pulse' : ''}`} />
                            <span className="hidden sm:inline">
                                {translatingIndexes.includes(sentenceIndex) ? 'Translating...' : 'Translate'}
                            </span>
                        </Button>
                    </div>
                </div>

                {/* Translations */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <label className="text-xs font-medium text-foreground">Translations</label>
                            <div className="flex items-center gap-2">
                                <Switch 
                                    checked={translationsExpanded}
                                    onCheckedChange={setTranslationsExpanded}
                                />
                                <span className="text-xs text-muted-foreground">
                                    {translationsExpanded ? 'Expanded' : `Collapsed (${sentence.translation?.length})`}
                                </span>
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addTranslation(sentenceIndex)}
                        >
                            <IconPlus className="h-3 w-3 mr-1" />
                            Add Translation
                        </Button>
                    </div>

                    {translationsExpanded && (
                        <>
                            {sentence.translation && sentence.translation.length > 0 ? (
                                <div className="space-y-2">
                                    {getSortedTranslations(sentence.translation).map((translation, translationIndex) => {
                                        // Find the original index for state updates
                                        const originalIndex = sentence.translation.findIndex(t => 
                                            t.language_code === translation.language_code && t.text === translation.text
                                        );
                                        return (
                                            <TranslationRow
                                                key={`${translation.language_code}-${translationIndex}`}
                                                translation={translation}
                                                sentenceIndex={sentenceIndex}
                                                originalIndex={originalIndex}
                                                updateTranslation={updateTranslation}
                                                deleteTranslation={deleteTranslation}
                                            />
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-6 border-2 border-dashed border-muted-foreground/25 rounded-md">
                                    <p className="text-xs text-muted-foreground">No translations yet</p>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => addTranslation(sentenceIndex)}
                                        className="mt-2"
                                    >
                                        <IconPlus className="h-3 w-3 mr-1" />
                                        Add First Translation
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default SentenceCard; 